import { DataItemFormat, DataItemImportance } from "../../misc/api/apiTypes";
import * as _ from 'lodash';
import { RequiredNotBlank } from './Required';
import { DataItemConfiguration } from "webportalclient";
import { hasValue } from "../../misc/Helpers/AnyHelper";

export function MinLengthValidator(value: string, minLength: number): string {
    return value && value.length < minLength ? `Minimum length must be ${minLength} characters.` : undefined;
}

export function MaxLengthValidator(value: string, maxLength: number): string {
    return value && value.length > maxLength ? `Maximum length must be ${maxLength} characters.` : undefined;
}

export function MinValueValidator(value: string, minValue: number, isMandatory: boolean): string {
    if (!isMandatory && !hasValue(value)) return undefined;

    return isNaN(+value) || +value < minValue ? `Minimum value is ${minValue}.` : undefined;
}

export function MaxValueValidator(value: string, maxValue: number, isMandatory: boolean): string {
    if (!isMandatory && !hasValue(value)) return undefined;

    return isNaN(+value) || +value > maxValue ? `Maximum value is ${maxValue}.` : undefined;
}

export function RequiredLengthValidator(value: string, length: number): string {
    return value && value.length !== length ? `Required length must be ${length} characters.` : undefined;
}

export function AlphaNumericFormatValidator(value: string, isMandatory: boolean): string {
    if (!isMandatory && !value) return undefined;

    return !value || !value.match(/^[a-z0-9 \r\n]+$/i) ? `The value should be alphanumeric.` : undefined;
}

function NumericFormatValidator(value: string | number, isMandatory: boolean): string {
    if (!isMandatory && !hasValue(value)) return undefined;

    return !hasValue(value) || !value.toString().match(/^[0-9]+([.][0-9]+)?$/i) ? `The value should be numeric.` : undefined;
}

function CustomFormatValidator(value: string, expectedFormatRegex: string): string {
    return value && !value.match(new RegExp(expectedFormatRegex)) ? `The value is not in the correct format` : undefined;
}

function FormatValidator(format: DataItemFormat, customFormat: string, isMandatory: boolean): (value: string) => string {
    switch (format) {
        case (DataItemFormat.Alphanumeric):
            return _.partialRight(AlphaNumericFormatValidator, isMandatory);
        case (DataItemFormat.Numeric):
            return _.partialRight(NumericFormatValidator, isMandatory);
        case (DataItemFormat.Custom):
            return _.partialRight(CustomFormatValidator, customFormat);
        default:
            throw "Invalid Format Validator Format Selected";
    }
}

function ValuePrefixedWithValidator(value: string, expectedSiteNumber: string): string {
    return !value || _.indexOf(value, expectedSiteNumber) !== 0 ? `Expected the value to begin with ${expectedSiteNumber}.` : undefined;
}

export interface ValidatorFunction {
    (val: string): string;
}

export interface ValidatorFunctions {
    (value: string): string;
}

/**
 *
 * Composing a single validator by combining other individual validators.
 * 
 * @param validators
 */
export function ConfigurableItemValidator(validators: ValidatorFunction[]): ValidatorFunctions {
    return (value: string): string => {
        const errors = _.compact(_.map(validators, ((validator: ValidatorFunction) => {
            return validator(value);
        })));

        return _.size(errors) > 0 ? _.first(errors) : undefined;
    }
}

/**
 * This is the function to generate validators  for the subect ID configurable field.
 *
 * Other configurable fields can be validated using the re-usable validators above.
 * 
 * @param dataItemConfiguration
 */
export function SubjectFieldValidator(dataItemConfiguration: DataItemConfiguration): ValidatorFunctions {
    const validators: ValidatorFunction[] = [];

    const isMandatory = dataItemConfiguration.importanceId === DataItemImportance.Mandatory;

    if (dataItemConfiguration.minimumLength !== undefined && dataItemConfiguration.minimumLength !== undefined) {
        validators.push(_.partialRight(MinLengthValidator, dataItemConfiguration.minimumLength));
        validators.push(_.partialRight(MaxLengthValidator, dataItemConfiguration.maximumLength));
    }
    else {
        validators.push(_.partialRight(RequiredLengthValidator, dataItemConfiguration.minimumLength));
    }

    validators.push(FormatValidator(dataItemConfiguration.formatId as DataItemFormat, dataItemConfiguration.customFormat, isMandatory));

    return ConfigurableItemValidator(validators);
}

export function ConfigFieldValidation(dataItemConfiguration: DataItemConfiguration, required: boolean, validate: boolean): ValidatorFunctions {
    const validators: ValidatorFunction[] = [];

    const isMandatory = dataItemConfiguration.importanceId === DataItemImportance.Mandatory;

    if (required ? isMandatory : false) {
        validators.push(RequiredNotBlank);
    }

    if (validate) {
        if (dataItemConfiguration.formatId)
            validators.push(FormatValidator(dataItemConfiguration.formatId as DataItemFormat, dataItemConfiguration.customFormat, isMandatory));

        if (dataItemConfiguration.minimumLength !== null) {
            validators.push(_.partialRight(MinLengthValidator, dataItemConfiguration.minimumLength));
            validators.push(_.partialRight(MaxLengthValidator, dataItemConfiguration.maximumLength));
        }

        if (hasValue(dataItemConfiguration.minimumValue))
            validators.push(_.partialRight(MinValueValidator, dataItemConfiguration.minimumValue, isMandatory));

        if (hasValue(dataItemConfiguration.maximumValue))
            validators.push(_.partialRight(MaxValueValidator, dataItemConfiguration.maximumValue, isMandatory));
    }

    return ConfigurableItemValidator(validators);
}
